import {
  Button,
  ButtonList,
  DropdownButton,
  Page,
  SearchInput,
  SortDirection,
} from '@dixa/lib-shared-habla';
import { QueueAgent } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Fragment, useEffect } from 'react';
import { useSetState } from 'react-use';

import { AgentsAddModal } from 'components/shared/agentsAddModal';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { WithQueueAgentsInjectedProps } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';

import { QueuesAgentsTable } from '../../containers/queuesAgentsTable';

const FETCH_DELAY = 1000;

export type QueuesAgentsOwnProps = {
  queueOfferAlgorithm: string;
  queueId: string;
  queueName: string;
};

type SortKey = 'display_name' | 'priority';

type QueuesAgentsProps = QueuesAgentsOwnProps & WithQueueAgentsInjectedProps;

type QueuesAgentsState = {
  isConfirmModalOpen: boolean;
  isAddAgentsModalOpen: boolean;
  currentAgent?: QueueAgent;
  waitingForFetch: boolean;
};

export const QueuesAgents = (props: QueuesAgentsProps): JSX.Element => {
  const [state, setState] = useSetState<QueuesAgentsState>({
    isConfirmModalOpen: false,
    isAddAgentsModalOpen: false,
    waitingForFetch: false,
  });

  const notify = useNotifications();

  useEffect(() => {
    props.queueAgents.getItems();
  }, []);

  const setWaitingForFetch = (waitingForFetch: boolean) => {
    setState({
      waitingForFetch,
    });
  };

  const delayedFetchItems = () => {
    const { queueAgents } = props;
    const { getItems } = queueAgents;
    setTimeout(() => {
      getItems().then(() => {
        setWaitingForFetch(false);
      });
    }, FETCH_DELAY);
  };

  const handleSearchSubmit = (searchTerm: string): void => {
    ampli.settingsQueueAgentSearchSubmitted();
    props.queueAgents.searchTermChange(searchTerm, 0);
  };

  const handleSearchToggle = (isOpen: boolean): void => {
    if (!isOpen && props.queueAgents.searchTerm) {
      handleSearch(null)();
    }
  };

  const handleSearch = (name: SortKey | null, direction?: SortDirection) => {
    return (): void => {
      const { queueAgents } = props;
      const { searchTermChange, sortByChange } = queueAgents;

      if (name) {
        sortByChange(name, direction);
      } else {
        searchTermChange('', 0);
      }

      ampli.settingsQueueAgentSorted({
        type: name ? `${name}:${direction ?? 'asc'}` : 'relevance',
      });
    };
  };

  const initAgentsFormValues = () => {
    const { queueAgents } = props;
    const { items } = queueAgents;

    const agents = items.map((item) => {
      return {
        agent_id: item.id,
        priority: item.priority,
      };
    });

    return { agents };
  };

  const gotoNextPage = () => {
    props.queueAgents.nextPage();
  };

  const gotoPreviousPage = () => {
    props.queueAgents.previousPage();
  };

  const handleRetry = () => {
    props.queueAgents.getItems();
  };

  const showRemoveModal = (agent: QueueAgent) => {
    setState({ isConfirmModalOpen: true, currentAgent: agent });
  };

  const hideRemoveModal = () => {
    setState({ isConfirmModalOpen: false });
  };

  const handleDeleteQueueAgent = (): void => {
    const { queueAgents } = props;
    const { remove } = queueAgents;
    const { currentAgent } = state;

    if (currentAgent) {
      setWaitingForFetch(true);
      remove([currentAgent.id])
        .then(() => {
          ampli.settingsQueueAgentDeleted();

          setState({ isConfirmModalOpen: false });
          const notificationOptions = {
            title: 'Agent removed',
            text: `"${currentAgent.name}" agent has been removed`,
            icon: 'i-done',
            className: 'success',
            time: 5000,
          };

          notify(notificationOptions);
          delayedFetchItems();
        })
        .catch((e) => {
          const notificationOptions = {
            title: 'An error occurred',
            text: `An error occurred when trying to remove "${currentAgent.name}" agent from queue`,
            icon: 'i-exclamation',
            className: 'danger',
          };

          notify(notificationOptions);
          setWaitingForFetch(false);
          console.error('Delete error', e);
        });
    }
  };

  const showAddAgentsModal = () => {
    setState({ isAddAgentsModalOpen: true });
  };

  const handleAddAgentsModalClose = (canceled = false) => {
    setState({ isAddAgentsModalOpen: false });

    if (!canceled) {
      setWaitingForFetch(true);
      delayedFetchItems();
    }
  };

  const handleQueuesAgentsPrioritySubmit = (values) => {
    const { queueAgents, queueName } = props;
    const { update } = queueAgents;

    if (values) {
      setWaitingForFetch(true);
      update(values.agents)
        .then((r) => {
          setState({ isConfirmModalOpen: false });
          const notificationOptions = {
            title: 'Queue updated',
            text: `Queue "${queueName}" updated`,
            className: 'success',
            time: 5000,
            icon: 'i-done',
          };

          notify(notificationOptions);
          delayedFetchItems();
        })
        .catch((e) => {
          const notificationOptions = {
            title: 'An error occurred',
            text: `An error occurred when trying to update the "${queueName}" queue`,
            icon: 'i-exclamation',
            className: 'danger',
          };

          notify(notificationOptions);
          setWaitingForFetch(false);
          console.error('Delete error', e);
        });
    }
  };

  const { queueAgents, queueOfferAlgorithm, queueId } = props;
  const { waitingForFetch } = state;
  const {
    getItemsAsyncStatus,
    items,
    hasNextPage,
    hasPreviousPage,
    updateAsyncStatus,
    searchTerm,
  } = queueAgents;

  return (
    <>
      <Page.Section
        title="All agents"
        controls={
          <>
            <SearchInput
              onSubmit={handleSearchSubmit}
              onToggle={handleSearchToggle}
            />

            <DropdownButton
              buttonProps={{ disabled: !!searchTerm }}
              dropdown={
                <ButtonList>
                  <ButtonList.Item
                    icon="arrow-up"
                    onClick={handleSearch('display_name', 'asc')}
                  >
                    A - Z
                  </ButtonList.Item>
                  <ButtonList.Item
                    icon="arrow-down"
                    onClick={handleSearch('display_name', 'desc')}
                  >
                    Z - A
                  </ButtonList.Item>
                  {queueOfferAlgorithm?.includes('agent_priority') && (
                    <Fragment>
                      <ButtonList.Divider />
                      <ButtonList.Item
                        icon="arrow-up"
                        onClick={handleSearch('priority', 'asc')}
                      >
                        Highest priority
                      </ButtonList.Item>
                      <ButtonList.Item
                        icon="arrow-down"
                        onClick={handleSearch('priority', 'desc')}
                      >
                        Lowest priority
                      </ButtonList.Item>
                    </Fragment>
                  )}
                </ButtonList>
              }
            >
              Sort by
            </DropdownButton>

            <Button onClick={showAddAgentsModal}>Add agent</Button>
          </>
        }
      >
        <QueuesAgentsTable
          initialValues={initAgentsFormValues()}
          onSubmit={handleQueuesAgentsPrioritySubmit}
          queueOfferAlgorithm={queueOfferAlgorithm}
          getItemsAsyncStatus={getItemsAsyncStatus}
          items={items}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          showRemoveModal={showRemoveModal}
          handleRetry={handleRetry}
          gotoNextPage={gotoNextPage}
          gotoPreviousPage={gotoPreviousPage}
          isLoading={waitingForFetch || updateAsyncStatus === 'loading'}
        />
      </Page.Section>

      <ConfirmModal
        isShown={state.isConfirmModalOpen}
        headline="Are you sure?"
        text={`Are you sure you want to remove "${state.currentAgent?.name}" agent from the queue?`}
        confirmText="Yes, delete it now"
        cancelText="Cancel"
        confirm={handleDeleteQueueAgent}
        cancel={hideRemoveModal}
      />

      <AgentsAddModal
        isOpen={state.isAddAgentsModalOpen}
        onClose={handleAddAgentsModalClose}
        addAgents={queueAgents.add}
        parentEntity="queue"
        queueId={queueId}
      />
    </>
  );
};
